import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import {
  taskApi, TaskDetailDto,
  timeEntryApi, TimeEntryDto,
} from 'api/services/timeTrackerApi';
import { useLocalAuth } from 'contexts/LocalAuthContext';
import { toDecimalHours, fromDecimalHours, formatHM } from './duration';

interface TaskDetailScreenProps {
  id: string;
  onBack?: () => void;
  /** When true, renders as modal content (no module-master-container/ModuleHeader). */
  modalMode?: boolean;
  /**
   * In modal mode, the parent can call this ref to trigger a dirty-aware close —
   * useful for backdrop clicks. If the log-time form or an inline edit has unsaved
   * input, a confirmation dialog appears instead of closing immediately.
   */
  closeGuardRef?: React.MutableRefObject<(() => void) | null>;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const TaskDetailScreen: React.FC<TaskDetailScreenProps> = ({ id: taskId, onBack, modalMode = false, closeGuardRef }) => {
  const { user } = useLocalAuth();
  const currentUserId = user?.id || '';

  const [task, setTask] = useState<TaskDetailDto | null>(null);
  const [loading, setLoading] = useState(false);

  // Add-entry form — date + notes + (hours, minutes) split for friendlier input
  const [addDate, setAddDate]   = useState(todayIso());
  const [addH, setAddH]         = useState<number>(1);
  const [addM, setAddM]         = useState<number>(0);
  const [addNotes, setAddNotes] = useState('');
  const [adding, setAdding]     = useState(false);

  // Edit-entry form — same split for the inline edit row
  const [editingId, setEditingId]     = useState<string | null>(null);
  const [editDate, setEditDate]       = useState(todayIso());
  const [editH, setEditH]             = useState<number>(0);
  const [editM, setEditM]             = useState<number>(0);
  const [editNotes, setEditNotes]     = useState('');
  const [editRowVersion, setEditRowVersion] = useState(0);
  const [savingEdit, setSavingEdit]   = useState(false);

  const [deleteOpen, setDeleteOpen]               = useState(false);
  const [deleting, setDeleting]                   = useState(false);
  const [pendingDelete, setPendingDelete]         = useState<TimeEntryDto | null>(null);

  const [closeConfirmOpen, setCloseConfirmOpen]   = useState(false);
  const [closing, setClosing]                     = useState(false);

  // Discard-changes guard — warn on close if the log-time form has content or
  // an inline edit is in progress.
  const [discardOpen, setDiscardOpen] = useState(false);
  const logDirty = useMemo(
    () => addNotes.trim() !== '' || addDate !== todayIso() || addH !== 1 || addM !== 0,
    [addNotes, addDate, addH, addM]
  );
  const editDirty = editingId !== null;
  const isDirty = logDirty || editDirty;

  const handleClose = useCallback(() => {
    if (isDirty) { setDiscardOpen(true); return; }
    onBack?.();
  }, [isDirty, onBack]);

  // Expose handleClose so parent modal wrappers can trigger a dirty-aware
  // close (e.g. from backdrop clicks).
  useEffect(() => {
    if (closeGuardRef) closeGuardRef.current = handleClose;
    return () => { if (closeGuardRef) closeGuardRef.current = null; };
  }, [closeGuardRef, handleClose]);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const d = await taskApi.getById(taskId);
      setTask(d);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err.message || 'Failed to load task.');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const isClosed = task?.status === 'Closed';

  // ── Close / Reopen ───────────────────────────────────────────────────
  const close = async () => {
    if (!task) return;
    setClosing(true);
    try {
      const t = await taskApi.close(task.id, task.rowVersion);
      setTask(t);
      notificationService.toast.success('Task closed.');
      setCloseConfirmOpen(false);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Close failed.');
    } finally {
      setClosing(false);
    }
  };

  const reopen = async () => {
    if (!task) return;
    try {
      const t = await taskApi.reopen(task.id, task.rowVersion);
      setTask(t);
      notificationService.toast.success('Task reopened.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Reopen failed.');
    }
  };

  // ── Add entry ────────────────────────────────────────────────────────
  const addEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task) return;
    const decimal = toDecimalHours(addH, addM);
    if (decimal <= 0) {
      notificationService.toast.error('Enter at least 1 minute.');
      return;
    }
    if (decimal > 24) {
      notificationService.toast.error('Time entry cannot exceed 24 hours.');
      return;
    }
    if (!addDate) {
      notificationService.toast.error('Date is required.');
      return;
    }
    setAdding(true);
    try {
      await timeEntryApi.createForTask(task.id, {
        entryDate: addDate,
        hours: decimal,
        notes: addNotes || null,
      });
      // Reset form
      setAddDate(todayIso());
      setAddH(1);
      setAddM(0);
      setAddNotes('');
      await load();
      notificationService.toast.success('Time entry added.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Add entry failed.');
    } finally {
      setAdding(false);
    }
  };

  // ── Edit entry ───────────────────────────────────────────────────────
  const startEdit = (entry: TimeEntryDto) => {
    const { h, m } = fromDecimalHours(entry.hours);
    setEditingId(entry.id);
    setEditDate(entry.entryDate.slice(0, 10));
    setEditH(h);
    setEditM(m);
    setEditNotes(entry.notes ?? '');
    setEditRowVersion(entry.rowVersion);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async () => {
    if (!editingId) return;
    const decimal = toDecimalHours(editH, editM);
    if (decimal <= 0) {
      notificationService.toast.error('Enter at least 1 minute.');
      return;
    }
    if (decimal > 24) {
      notificationService.toast.error('Time entry cannot exceed 24 hours.');
      return;
    }
    setSavingEdit(true);
    try {
      await timeEntryApi.update(editingId, {
        entryDate: editDate,
        hours: decimal,
        notes: editNotes || null,
        rowVersion: editRowVersion,
      });
      setEditingId(null);
      await load();
      notificationService.toast.success('Time entry updated.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Update failed.');
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Delete entry ─────────────────────────────────────────────────────
  const requestDelete = (entry: TimeEntryDto) => {
    setPendingDelete(entry);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await timeEntryApi.remove(pendingDelete.id, pendingDelete.rowVersion);
      setDeleteOpen(false);
      setPendingDelete(null);
      await load();
      notificationService.toast.success('Time entry deleted.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const goBack = () => { handleClose(); };

  // ── Grid columns for time entries ───────────────────────────────────
  type EntryRow = TimeEntryDto & { id: string };
  const entryColumns: SyutGridColumn<EntryRow>[] = useMemo(() => [
    { id: 'entryDate', header: 'Date', accessorKey: 'entryDate', size: 130,
      cell: ({ row, getValue }) => {
        if (editingId === row.original.id) {
          return (
            <input
              type="date"
              className="form-input"
              style={{ height: 32 }}
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
            />
          );
        }
        return (getValue() as string)?.slice(0, 10) ?? '—';
      },
    },
    { id: 'userName', header: 'User', accessorKey: 'userName', size: 160,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'hours', header: 'Duration', accessorKey: 'hours', size: 170,
      cell: ({ row, getValue }) => {
        if (editingId === row.original.id) {
          return (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="number"
                className="form-input"
                style={{ height: 32, width: 50, padding: '0 6px' }}
                min={0} max={23} step={1}
                value={editH}
                onChange={(e) => setEditH(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                aria-label="Hours"
              />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>h</span>
              <input
                type="number"
                className="form-input"
                style={{ height: 32, width: 56, padding: '0 6px' }}
                min={0} max={59} step={1}
                value={editM}
                onChange={(e) => setEditM(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                aria-label="Minutes"
              />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>m</span>
            </div>
          );
        }
        return formatHM(Number(getValue() ?? 0));
      },
    },
    { id: 'notes', header: 'Notes', accessorKey: 'notes', size: 280, enableSorting: false,
      cell: ({ row, getValue }) => {
        if (editingId === row.original.id) {
          return (
            <input
              className="form-input"
              style={{ height: 32 }}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
            />
          );
        }
        return (getValue() as string) || '—';
      },
    },
  ], [editingId, editDate, editH, editM, editNotes]);

  const entryRowActions = useCallback((row: EntryRow): ActionMenuItem[] => {
    const isOwner = row.userId === currentUserId;
    if (!isOwner || isClosed) return [];
    if (editingId === row.id) {
      return [
        { id: 'save',   label: savingEdit ? 'Saving…' : 'Save', icon: <IconSystem.save size={16} />, onClick: saveEdit,   disabled: savingEdit },
        { id: 'cancel', label: 'Cancel',                        icon: <IconSystem.x    size={16} />, onClick: cancelEdit },
      ];
    }
    return [
      { id: 'edit',   label: 'Edit',   icon: <IconSystem.edit  size={16} />, onClick: () => startEdit(row) },
      { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => requestDelete(row), variant: 'danger' },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, isClosed, editingId, editH, editM, editDate, editNotes, savingEdit]);

  const actions: ActionButton[] = useMemo(() => {
    const a: ActionButton[] = [];
    if (!modalMode) {
      a.push({ id: 'back', icon: <IconSystem.arrowLeft size={18} />, label: 'Back to tasks', onClick: goBack, tooltip: 'Back', variant: 'icon' });
    }
    a.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: load, tooltip: 'Refresh', variant: 'icon' });
    if (task) {
      a.push(
        isClosed
          ? { id: 'reopen', icon: <IconSystem.unlock size={18} />, label: 'Reopen', onClick: reopen, tooltip: 'Reopen this task', variant: 'icon' }
          : { id: 'close',  icon: <IconSystem.lock   size={18} />, label: 'Close',  onClick: () => setCloseConfirmOpen(true), tooltip: 'Close this task', variant: 'danger' }
      );
    }
    return a;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, isClosed, modalMode]);

  if (loading && !task) {
    if (modalMode) {
      return (
        <>
          <div className="modal-header" style={{ minHeight: 'auto' }}>
            <h2 className="modal-title">Loading…</h2>
            <button className="modal-close" onClick={onBack}><IconSystem.x size={18} /></button>
          </div>
          <div className="modal-content" />
        </>
      );
    }
    return (
      <div className="module-master-container">
        <ModuleHeader title="Task Detail" description="Loading…" icon={<IconSystem.list size={18} />} />
      </div>
    );
  }

  if (!task) {
    if (modalMode) {
      return (
        <>
          <div className="modal-header" style={{ minHeight: 'auto' }}>
            <h2 className="modal-title">Task not found</h2>
            <button className="modal-close" onClick={onBack}><IconSystem.x size={18} /></button>
          </div>
          <div className="modal-content" />
        </>
      );
    }
    return (
      <div className="module-master-container">
        <ModuleHeader title="Task Detail" description="Task not found." icon={<IconSystem.list size={18} />} />
      </div>
    );
  }

  const bodyContent = (
    <div style={{ flex: 1, display: 'flex', gap: '0.75rem', padding: '0.75rem', overflow: 'auto' }}>
          {/* ── Main column ───────────────────────────────────────────── */}
          <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: '0.75rem', minWidth: 0 }}>
            <div style={{
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              display: 'flex', alignItems: 'center', gap: '0.75rem',
            }}>
              <span className={`badge ${isClosed ? 'badge-inactive' : 'badge-active'}`}>{task.status}</span>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Created by {task.createdUserName || '—'}
                {task.closedAt && <> · Closed by {task.closedUserName || '—'} on {task.closedAt.slice(0,10)}</>}
              </div>
              <div style={{ marginLeft: 'auto', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Total: <strong style={{ color: 'var(--text-primary)' }}>{formatHM(task.totalHours)}</strong>
              </div>
            </div>

            {task.description && (
              <div style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                fontSize: '0.875rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap',
              }}>
                {task.description}
              </div>
            )}

            {/* Add entry form */}
            {!isClosed ? (
              <form onSubmit={addEntry} style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
              }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                  Log time
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ minWidth: 140 }}>
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={addDate}
                      onChange={(e) => setAddDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="form-label">Duration</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="number"
                        className="form-input"
                        style={{ width: 70 }}
                        min={0} max={23} step={1}
                        value={addH}
                        onChange={(e) => setAddH(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
                        aria-label="Hours"
                      />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>h</span>
                      <input
                        type="number"
                        className="form-input"
                        style={{ width: 70 }}
                        min={0} max={59} step={5}
                        value={addM}
                        onChange={(e) => setAddM(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
                        aria-label="Minutes"
                      />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>m</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label className="form-label">Notes</label>
                    <input
                      className="form-input"
                      placeholder="What did you work on?"
                      value={addNotes}
                      onChange={(e) => setAddNotes(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={adding || !addDate || (addH === 0 && addM === 0)}
                  >
                    {adding ? 'Adding…' : 'Add'}
                  </button>
                </div>
              </form>
            ) : (
              <div style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.625rem 0.875rem',
                fontSize: '0.8125rem', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
              }}>
                <IconSystem.lock size={14} /> Task is closed. Reopen it to log additional time.
              </div>
            )}

            {/* Entries grid */}
            <div style={{ flex: 1, minHeight: 240 }}>
              <SyutGrid
                data={task.timeEntries as EntryRow[]}
                columns={entryColumns}
                enablePagination={false}
                rowActions={entryRowActions}
                emptyMessage="No time entries yet."
              />
            </div>

            {/* Status history timeline */}
            {(task.statusHistory && task.statusHistory.length > 0) && (
              <div style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem' }}>
                  <IconSystem.clock size={16} color="var(--primary)" />
                  <strong style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>Status history</strong>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    ({task.statusHistory.length} event{task.statusHistory.length === 1 ? '' : 's'})
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {task.statusHistory.map(h => (
                    <div key={h.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
                      padding: '0.375rem 0',
                      fontSize: '0.8125rem',
                    }}>
                      <span style={{
                        flexShrink: 0, marginTop: 5,
                        width: 8, height: 8, borderRadius: '50%',
                        background: h.transition === 'Closed' ? 'var(--danger)' : 'var(--primary)',
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{h.transition}</span>
                        <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                          {new Date(h.changedAt).toLocaleString()} · {h.changedByName || h.changedById?.slice(0, 8) || '—'}
                        </span>
                        {h.note && (
                          <div style={{ color: 'var(--text-secondary)', marginTop: '0.125rem' }}>{h.note}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar: hours by user (only when multiple contributors) ── */}
          {task.hoursByUser.length > 1 && (
            <aside style={{
              width: 260, flexShrink: 0,
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '0.5rem',
              padding: '0.875rem 1rem',
              height: 'fit-content',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <IconSystem.users size={16} color="var(--primary)" />
                <strong style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>Hours by user</strong>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {task.hoursByUser.map(u => (
                  <div key={u.userId} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '0.375rem 0',
                    borderBottom: '1px solid var(--border-color)',
                    fontSize: '0.8125rem',
                  }}>
                    <span style={{ color: 'var(--text-primary)' }}>{u.userName || u.userId.slice(0, 8)}</span>
                    <strong style={{ color: 'var(--primary)' }}>{formatHM(u.hours)}</strong>
                  </div>
                ))}
              </div>
            </aside>
          )}
    </div>
  );

  const dialogs = (
    <>
      <ConfirmDialog
        open={closeConfirmOpen}
        title="Close Task"
        message={`Close "${task.title}"? You can reopen it later if needed.`}
        confirmLabel="Close task"
        cancelLabel="Cancel"
        variant="danger"
        loading={closing}
        loadingMessage="Closing…"
        onConfirm={close}
        onCancel={() => setCloseConfirmOpen(false)}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Time Entry"
        message={`Delete the time entry on ${pendingDelete?.entryDate?.slice(0,10)} (${formatHM(pendingDelete?.hours ?? 0)})?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting…"
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteOpen(false); setPendingDelete(null); }}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message={editDirty
          ? 'An inline edit is in progress. Discard the edit?'
          : 'You have unsaved content in the log-time form. Discard it?'}
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onBack?.(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );

  if (modalMode) {
    return (
      <>
        <div className="modal-header" style={{ minHeight: 'auto', gap: '0.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <IconSystem.list size={16} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</span>
            </h2>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {task.clientName}{task.projectName ? ` · ${task.projectName}` : ''} · {task.taskTypeName}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
            <button
              className="modal-header-btn"
              onClick={load}
              title="Refresh"
              disabled={loading}
            >
              <IconSystem.refresh size={16} />
            </button>
            {isClosed ? (
              <button className="modal-header-btn" onClick={reopen} title="Reopen this task">
                <IconSystem.unlock size={16} />
              </button>
            ) : (
              <button
                className="modal-header-btn"
                onClick={() => setCloseConfirmOpen(true)}
                title="Close this task"
                style={{ color: 'var(--danger)' }}
              >
                <IconSystem.lock size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} title="Close">
              <IconSystem.x size={18} />
            </button>
          </div>
        </div>
        <div className="modal-content" style={{ padding: 0, overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
          {bodyContent}
        </div>
        {dialogs}
      </>
    );
  }

  return (
    <div className="module-master-container">
      <ModuleHeader
        title={task.title}
        description={`${task.clientName}${task.projectName ? ` · ${task.projectName}` : ''} · ${task.taskTypeName}`}
        icon={<IconSystem.list size={18} />}
        actions={actions}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        {bodyContent}
      </div>

      {dialogs}
    </div>
  );
};

export default TaskDetailScreen;
